import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Shield, ArrowLeft } from 'lucide-react';
import { useTranslations } from '../../hooks/useTranslations';
import { Header } from '../Header';
import { Footer } from '../Footer';
import { translations } from '../../locales';
import { getLocalizedPath, type LocalizedBasePath } from '../../utils/localizedRoutes';

export function PrivacyPolicyPage() {
  const navigate = useNavigate();
  const { t, currentLanguage } = useTranslations();
  
  const buildUrl = (path: LocalizedBasePath) => {
    return getLocalizedPath(path, currentLanguage);
  };

  // Get privacy translations, fallback to English if not available
  const privacyData = (t as any).pages?.privacy;
  const englishPrivacy = (translations.en as any).pages?.privacy;
  const p = privacyData || englishPrivacy;
  
  // Debug: log if translations are missing
  if (import.meta.env.DEV) {
    console.log('Current language:', currentLanguage);
    console.log('Privacy data exists:', !!privacyData);
    console.log('English privacy exists:', !!englishPrivacy);
    console.log('Using translations:', privacyData ? currentLanguage : 'en (fallback)');
    if (!privacyData) {
      console.log('Available keys in t:', Object.keys(t));
      console.log('Has pages:', !!(t as any).pages);
      if ((t as any).pages) {
        console.log('Pages keys:', Object.keys((t as any).pages));
      }
    }
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-blue-800" style={{ paddingTop: '140px' }}>
        <div className="container mx-auto px-4 py-12 max-w-4xl">
          {/* Back Button */}
          <button
            onClick={() => navigate(buildUrl('/'))}
            className="flex items-center text-white hover:text-purple-200 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span>Back to Home</span>
          </button>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-white text-4xl">{p?.title || 'Privacy Policy'}</h1>
                <p className="text-purple-100">{p?.lastUpdated || 'Last updated: December 12, 2025'}</p>
              </div>
            </div>
          </div>

          <Card className="bg-white/95 backdrop-blur-sm shadow-xl">
            <CardContent className="p-8 space-y-8">
              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.interpretationAndDefinitions || '1. Interpretation and Definitions'}</h2>
                <h3 className="text-gray-800 mb-2">{p?.interpretation || 'Interpretation'}</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  {p?.interpretationDesc || ''}
                </p>
                <h3 className="text-gray-800 mb-2">{p?.definitions || 'Definitions'}</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.definitionsDesc || ''}
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  {(p?.definitionsItems || []).map((item: any, index: number) => (
                    <li key={index} className="mb-2">
                      <strong>{item?.term || ''}</strong> {item?.definition || ''}
                    </li>
                  ))}
                </ul>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.scope || '2. Scope of This Privacy Policy'}</h2>
                <p className="text-gray-700 leading-relaxed">
                  {p?.scopeDesc || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.dataController || '3. Data Controller'}</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.dataControllerDesc || ''}
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700">
                    <strong>{p?.companyName || 'General Secret OÜ'}</strong><br />
                    {p?.companyAddress || ''}<br />
                    {p?.dataControllerEmail || ''}
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.collectingAndUsing || '4. Collecting and Using Your Personal Data'}</h2>
                <h3 className="text-gray-800 mb-2">{p?.typesOfDataCollected || '4.1 Types of Data Collected'}</h3>
                <h4 className="text-gray-700 mb-2 font-semibold">{p?.personalData || 'Personal Data'}</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.personalDataDesc || ''}
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
                  {(p?.personalDataItems || []).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
                <h4 className="text-gray-700 mb-2 font-semibold">{p?.usageData || 'Usage Data'}</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.usageDataDesc || ''}
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  {(p?.usageDataItems || []).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.purposesAndLegalBasis || '5. Purposes and Legal Basis for Processing'}</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.purposesAndLegalBasisDesc || ''}
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-3 ml-4">
                  {(p?.purposesAndLegalBasisItems || []).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  {p?.purposesAndLegalBasisLegalBases || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.thirdPartyServices || '6. Third-Party Services'}</h2>
                <h3 className="text-gray-800 mb-2">{p?.analytics || '6.1 Analytics'}</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>{p?.googleAnalytics || 'Google Analytics 4 (Google Ireland Limited)'}</strong><br />
                  {p?.googleAnalyticsDesc || ''}
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
                  <li>{p?.googleAnalyticsData || ''}</li>
                  <li>{p?.googleAnalyticsPlace || ''}</li>
                  <li>{p?.googleAnalyticsOptOut || ''}</li>
                </ul>
                <h3 className="text-gray-800 mb-2">{p?.advertising || '6.2 Advertising'}</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>{p?.googleAdManager || 'Google Ad Manager (Google Ireland Limited)'}</strong><br />
                  {p?.googleAdManagerDesc || ''}
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
                  <li>{p?.googleAdManagerData || ''}</li>
                  <li>{p?.googleAdManagerPlace || ''}</li>
                  <li>{p?.googleAdManagerSettings || ''}</li>
                </ul>
                <h3 className="text-gray-800 mb-2">{p?.payments || '6.3 Payments'}</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.paymentsDesc || ''}
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>{p?.stripe || 'Stripe'}</strong><br />
                  {p?.stripeDesc || ''}
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
                  <li>{p?.stripeData || ''}</li>
                  <li>{p?.stripePlace || ''}</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>{p?.revolut || 'Revolut Payments'}</strong><br />
                  {p?.revolutDesc || ''}
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>{p?.revolutData || ''}</li>
                  <li>{p?.revolutPlace || ''}</li>
                </ul>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.cookiesAndTracking || '7. Cookies and Tracking Technologies'}</h2>
                <p className="text-gray-700 leading-relaxed">
                  {p?.cookiesAndTrackingDesc || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.sharingOfData || '8. Sharing of Your Personal Data'}</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.sharingOfDataDesc1 || ''}
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.sharingOfDataDesc2 || ''}
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-3 ml-4">
                  {(p?.sharingOfDataItems || []).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  {p?.sharingOfDataDesc3 || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.dataRetention || '9. Data Retention'}</h2>
                <p className="text-gray-700 leading-relaxed">
                  {p?.dataRetentionDesc || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.internationalTransfers || '10. International Data Transfers'}</h2>
                <p className="text-gray-700 leading-relaxed">
                  {p?.internationalTransfersDesc || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.yourRightsGDPR || '11. Your Rights Under GDPR'}</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.yourRightsGDPRDesc || ''}
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-3 ml-4">
                  {(p?.yourRightsGDPRItems || []).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  {p?.yourRightsGDPRContact || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.deletionOfData || '12. Deletion of Personal Data'}</h2>
                <p className="text-gray-700 leading-relaxed">
                  {p?.deletionOfDataDesc || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.disclosureOfData || '13. Disclosure of Personal Data'}</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.disclosureOfDataDesc || ''}
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  {(p?.disclosureOfDataItems || []).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.securityOfData || '14. Security of Your Personal Data'}</h2>
                <p className="text-gray-700 leading-relaxed">
                  {p?.securityOfDataDesc || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.childrenPrivacy || '15. Children\'s Privacy'}</h2>
                <p className="text-gray-700 leading-relaxed">
                  {p?.childrenPrivacyDesc || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.linksToOtherWebsites || '16. Links to Other Websites'}</h2>
                <p className="text-gray-700 leading-relaxed">
                  {p?.linksToOtherWebsitesDesc || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.changesToPolicy || '17. Changes to This Privacy Policy'}</h2>
                <p className="text-gray-700 leading-relaxed">
                  {p?.changesToPolicyDesc || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.contactUs || '18. Contact Us'}</h2>
                <p className="text-gray-700 leading-relaxed mb-2">
                  {p?.contactUsDesc || ''}
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700">
                    {p?.contactEmail || ''}<br />
                    {p?.contactCompany || ''}<br />
                    {p?.contactAddress || ''}
                  </p>
                </div>
              </section>
            </CardContent>
          </Card>
        </div>
        <Footer onShowBlog={() => navigate(buildUrl('/blog'))} />
      </div>
    </>
  );
}
